import type { FunctionComponent } from 'react'
import React, { useContext, useEffect, useState } from 'react'
import { usePreviousDistinct } from 'react-use'

import classnames from 'classnames'
import { CarouselContext, Slide, Slider } from 'pure-react-carousel'

import type { Images } from '../../imageType'
import styles from './Carousel.module.scss'
import { isLargeJump } from './Carousel.utils'
import { CarouselPortal } from './CarouselPortal'
import { SlideImage } from './SlideImage'

export const Carousel: FunctionComponent<Props> = ({
  carouselBgColor,
  current,
  images,
  onChange,
  portalZIndex,
}) => {
  const carouselContext = useContext(CarouselContext)
  const previousIndex = usePreviousDistinct(current) ?? current + 1

  const [portalConfig, setPortalConfig] = useState<{
    zoomSrc?: Images['zoomSrc']
    zoomSources?: Images['zoomSources']
    alt?: Images['alt']
    displayPortal: boolean
  }>({ displayPortal: false })

  useEffect(() => {
    const subscription = () => onChange(carouselContext.state.currentSlide)
    carouselContext.subscribe(subscription)
    return () => carouselContext.unsubscribe(subscription)
  }, [carouselContext, onChange])

  return (
    <section className={styles.container}>
      {portalConfig.displayPortal && (
        <CarouselPortal
          setPortalConfig={setPortalConfig}
          zoomSrc={portalConfig.zoomSrc}
          zoomSources={portalConfig.zoomSources}
          portalZIndex={portalZIndex}
        />
      )}
      <Slider
        classNameTray={classnames(
          styles.slider,
          isLargeJump(current, previousIndex) && styles.withoutAnimation
        )}
        aria-label="Carousel"
      >
        {images.map(
          ({ src, alt, credit, sources, zoomSources, zoomSrc, zoomWidth, zoomHeight }, index) => (
            <Slide
              tabIndex={-1}
              key={`image_${src}`}
              index={index}
              className={styles.slide}
              aria-hidden="true"
            >
              <SlideImage
                {...{
                  alt,
                  credit,
                  src,
                  sources,
                  zoomSources,
                  zoomSrc,
                  zoomWidth,
                  zoomHeight,
                  setPortalConfig,
                  carouselBgColor,
                }}
              />
            </Slide>
          )
        )}
      </Slider>
    </section>
  )
}

///////// IMPLEMENTATION /////////

type Props = {
  /**
   * Background colour behind main image
   */
  carouselBgColor: 'grey' | 'white'
  /**
   * Current slide number
   */
  current: number
  /**
   * Array of image objects
   */
  images: Array<Images>
  /**
   * z-index applied to the carousel portal
   */
  portalZIndex?: number
  /**
   * Function to handle on change
   */
  onChange: (num: number) => void
}
